import type { ErrorLike } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import {
  GetMboClassesByDateRangeDocument,
  GetMboClassesByDateRangeQuery,
  MboClassDetailsFragment,
} from '../graphql/types';
import { inclusiveDateRange } from '../utils/date';
import { useClassesQueryVariables } from './useQueryParams';

export const PAGE_SIZE = 15;

interface MboClassesQueryResults {
  error: ErrorLike | undefined;
  loading: boolean;
  initialLoading: boolean;
  isFetchingMore: boolean;
  hasNextPage: boolean | undefined;
  data: GetMboClassesByDateRangeQuery | undefined;
  classCount: number | undefined;
  classes: MboClassDetailsFragment[] | undefined;
  loadMoreClasses: () => Promise<void>;
}

export default (): MboClassesQueryResults => {
  const [{ locationIds, studioIds, dateRange }] = useClassesQueryVariables();
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isChangingFilters, setIsChangingFilters] = useState(false);

  const dates = useMemo(() => inclusiveDateRange(dateRange), [dateRange]);

  const queryVariables = useMemo(
    () => ({
      filter: {
        locationIds,
        studioIds,
        dates,
      },
      input: {
        first: PAGE_SIZE,
      },
    }),
    [locationIds, studioIds, dates],
  );

  const [fetch, { data, loading, error, fetchMore }] = useLazyQuery(
    GetMboClassesByDateRangeDocument,
    {
      fetchPolicy: 'cache-first',
    },
  );

  useEffect(() => {
    setIsChangingFilters(true);
    fetch({ variables: queryVariables }).finally(() => {
      setIsChangingFilters(false);
    });
  }, [fetch, queryVariables]);

  const hasNextPage = data?.availableClasses?.pageInfo?.hasNextPage;

  const loadMoreClasses = async () => {
    if (!hasNextPage) {
      setIsFetchingMore(false);
      return;
    }
    setIsFetchingMore(true);

    await fetchMore({
      variables: {
        filter: {
          locationIds,
          studioIds,
          dates,
        },
        input: {
          first: PAGE_SIZE,
          after: data?.availableClasses?.pageInfo?.endCursor,
        },
      },
    });
    setIsFetchingMore(false);
  };

  const classCount = data?.availableClasses.count;

  const classes = data?.availableClasses?.nodes;

  const initialLoading = loading && (!classes || classes.length === 0);

  return {
    error,
    loading: loading || isChangingFilters,
    initialLoading,
    isFetchingMore,
    hasNextPage,
    data,
    classCount,
    classes,
    loadMoreClasses,
  };
};
